import React, { useSyncExternalStore, useState, type CSSProperties } from 'react';
import {
  REACTOR_UNLOCK_THRESHOLD,
  MODIFIER_DEFS,
  SEASON_MILESTONES,
} from '@cradle7/shared';
import { useRunStore } from '../../store/runStore';
import { unlockAudio, sfxChallengeStart } from '../../game/audio';
import { useT, type TKey } from '../../i18n';
import { haptic } from '../../telegram';
import { useRunHistorySnapshot } from '../../runHistory';
import { useCatStore } from '../../catStore';
import { useChallenge } from '../../challengeStore';
import { useTournament } from '../../tournamentStore';
import { getAchievements, subscribeProgress } from '../../progressStore';
import { useSeasonData, setSeasonData } from '../../seasonStore';
import { api } from '../../api';
import { GlyphDefs, SectionHead } from '../design/atoms';
import { StreakRing } from '../StreakRing';
import { QuestList } from '../QuestList';
import { NewsTicker } from '../decor/NewsTicker';

// ─── CatHero ─────────────────────────────────────────────────────────────────
// Verbatim port of the Claude Design hero portrait.
// Pure presentational — no data wiring needed.

function CatHero() {
  return (
    <div style={{
      position: 'relative',
      width: 200, height: 200, margin: '0 auto',
    }}>
      {/* far halo */}
      <div style={{
        position: 'absolute', inset: -10, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(245,197,66,0.10) 0%, transparent 55%)',
        animation: 'pulse-glow 6s ease-in-out infinite',
      }} />
      {/* rotating scan ring */}
      <div className="hero-scan-ring" style={{
        position: 'absolute', inset: 4, borderRadius: '50%',
        pointerEvents: 'none',
      }}/>
      {/* orbiting particles */}
      <div className="hero-orbit" style={{
        position: 'absolute', inset: -4, borderRadius: '50%',
        pointerEvents: 'none',
      }}>
        <span style={{
          position: 'absolute', top: '50%', left: -3,
          width: 6, height: 6, borderRadius: '50%',
          background: '#3dd5ff', boxShadow: '0 0 8px #3dd5ff',
        }}/>
      </div>
      <div className="hero-orbit hero-orbit-2" style={{
        position: 'absolute', inset: -8, borderRadius: '50%',
        pointerEvents: 'none',
      }}>
        <span style={{
          position: 'absolute', top: '50%', left: -2,
          width: 4, height: 4, borderRadius: '50%',
          background: '#f5c542', boxShadow: '0 0 6px #f5c542',
        }}/>
      </div>
      {/* concentric rings + cat silhouette SVG */}
      <svg width="200" height="200" viewBox="0 0 260 260" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <radialGradient id="medBg" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#243450"/>
            <stop offset="60%" stopColor="#0e1830"/>
            <stop offset="100%" stopColor="#04081a"/>
          </radialGradient>
          <linearGradient id="ringG" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffe48a"/>
            <stop offset="100%" stopColor="#7a4f08"/>
          </linearGradient>
          <pattern id="hexPat" x="0" y="0" width="14" height="12" patternUnits="userSpaceOnUse">
            <path d="M0 6 L 7 0 L 14 6 L 7 12 Z" fill="none" stroke="rgba(61,213,255,0.10)" strokeWidth="0.5"/>
          </pattern>
          <clipPath id="medClip"><circle cx="130" cy="130" r="96"/></clipPath>
          <radialGradient id="rimGlow" cx="50%" cy="0%" r="80%">
            <stop offset="0%" stopColor="rgba(245,197,66,0.45)"/>
            <stop offset="100%" stopColor="rgba(245,197,66,0)"/>
          </radialGradient>
        </defs>
        {/* outer dashed ring */}
        <circle cx="130" cy="130" r="118" fill="none" stroke="#3dd5ff" strokeWidth="0.8" strokeDasharray="2 6" opacity="0.5"/>
        {/* gold ring with tick marks */}
        <circle cx="130" cy="130" r="108" fill="none" stroke="url(#ringG)" strokeWidth="2"/>
        {[...Array(12)].map((_, i) => {
          const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
          const x1 = 130 + Math.cos(a) * 100;
          const y1 = 130 + Math.sin(a) * 100;
          const x2 = 130 + Math.cos(a) * 108;
          const y2 = 130 + Math.sin(a) * 108;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#f5c542" strokeWidth="1.2"/>;
        })}
        {/* inner medallion fill */}
        <circle cx="130" cy="130" r="96" fill="url(#medBg)" stroke="#3dd5ff" strokeWidth="0.8" strokeOpacity="0.55"/>
        {/* hex grid pattern */}
        <rect x="34" y="34" width="192" height="192" fill="url(#hexPat)" clipPath="url(#medClip)"/>
        {/* CAT silhouette */}
        <g clipPath="url(#medClip)">
          {/* shoulder/body */}
          <path d="M70 230 C 70 175, 80 145, 130 130 C 180 145, 190 175, 190 230 Z"
                fill="#0a1226" stroke="#3dd5ff" strokeWidth="1" strokeOpacity="0.6"/>
          {/* chest plate */}
          <rect x="105" y="170" width="50" height="60" rx="8" fill="rgba(255,255,255,0.04)" stroke="#f5c542" strokeWidth="1" strokeOpacity="0.5"/>
          <circle cx="130" cy="195" r="11" fill="#0a1226" stroke="#f5c542" strokeWidth="1.5"/>
          <circle cx="130" cy="195" r="5" fill="#f5c542" className="hero-core"/>
          {/* head */}
          <ellipse cx="130" cy="105" rx="52" ry="48" fill="#10182b" stroke="#3dd5ff" strokeWidth="1" strokeOpacity="0.7"/>
          {/* ears */}
          <path d="M85 90 L 75 45 L 105 80 Z" fill="#0a1226" stroke="#3dd5ff" strokeWidth="1" strokeOpacity="0.7"/>
          <path d="M175 90 L 185 45 L 155 80 Z" fill="#0a1226" stroke="#3dd5ff" strokeWidth="1" strokeOpacity="0.7"/>
          {/* inner ear glow */}
          <path d="M88 80 L 82 55 L 100 78 Z" fill="#f5c542" opacity="0.35"/>
          <path d="M172 80 L 178 55 L 160 78 Z" fill="#f5c542" opacity="0.35"/>
          {/* visor */}
          <rect x="92" y="95" width="76" height="18" rx="4" fill="#000"/>
          <rect x="96" y="98.5" width="32" height="11" rx="2" fill="#3dd5ff" className="hero-visor"/>
          <rect x="132" y="98.5" width="32" height="11" rx="2" fill="#3dd5ff" className="hero-visor"/>
          {/* visor highlights */}
          <rect x="98" y="100" width="10" height="3" fill="rgba(255,255,255,0.7)"/>
          <rect x="134" y="100" width="10" height="3" fill="rgba(255,255,255,0.7)"/>
          {/* nose */}
          <path d="M124 122 L 136 122 L 130 128 Z" fill="#f5c542"/>
          {/* whisker sensor dots */}
          <circle cx="98" cy="130" r="1.4" fill="#3dd5ff"/>
          <circle cx="108" cy="135" r="1.4" fill="#3dd5ff"/>
          <circle cx="162" cy="130" r="1.4" fill="#3dd5ff"/>
          <circle cx="152" cy="135" r="1.4" fill="#3dd5ff"/>
          {/* rim light wash */}
          <ellipse cx="130" cy="55" rx="80" ry="40" fill="url(#rimGlow)"/>
        </g>
        {/* mech corner brackets */}
        {([[28,28,0],[232,28,90],[232,232,180],[28,232,270]] as const).map(([x,y,r],i) => (
          <g key={i} transform={`translate(${x} ${y}) rotate(${r})`}>
            <path d="M0 10 L 0 0 L 10 0" fill="none" stroke="#f5c542" strokeWidth="2"/>
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── StatTile ─────────────────────────────────────────────────────────────────
// Small card: icon + label + value + accent color.

type StatTileProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent: string;
};

function StatTile({ icon, label, value, accent }: StatTileProps) {
  return (
    <div className="chunk" style={{
      flex: 1, padding: '10px 12px',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        display: 'grid', placeItems: 'center',
        background: `linear-gradient(180deg, ${accent}33, ${accent}11)`,
        boxShadow: `inset 0 0 0 1px ${accent}55`,
        color: accent,
        flexShrink: 0,
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--f-display)', fontSize: 16, lineHeight: 1, color: '#fff',
        }}>{value}</div>
        <div style={{
          fontSize: 9.5, color: 'var(--tx-faint)',
          marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.1em',
          fontFamily: 'var(--f-display)',
        }}>{label}</div>
      </div>
    </div>
  );
}

// ─── SeasonStrip ─────────────────────────────────────────────────────────────
// Battle-pass-style progress strip. Wired to seasonStore (Phase 3l).

// Icon glyphs per milestone index (cycles through the 4 defined milestones).
const MILESTONE_ICONS = ['◗', '★', '▣', '♛'] as const;
// Accent colours per milestone index.
const MILESTONE_COLS = ['#f5c542', '#b76bff', '#3dd5ff', '#ff9a3c'] as const;
// Maximum level for the progress bar range (last defined milestone).
const MAX_LEVEL = (SEASON_MILESTONES[SEASON_MILESTONES.length - 1] ?? { level: 10 }).level;

function SeasonStrip() {
  const { t } = useT();
  const season = useSeasonData();
  const [claiming, setClaiming] = useState(false);

  const handleClaim = async () => {
    if (!season || season.claimableLevels.length === 0 || claiming) return;
    const levelToClaim = season.claimableLevels[0];
    if (levelToClaim === undefined) return;
    setClaiming(true);
    haptic.impact('medium');
    try {
      const result = await api.claimMilestone(levelToClaim);
      // Re-fetch season to get updated xp/level/claimableLevels, plus update
      // fishies balance by triggering a fresh season hydrate.
      const fresh = await api.getSeason().catch(() => null);
      if (fresh) {
        setSeasonData({ ...fresh, claimedLevels: result.claimedLevels });
      }
    } catch {
      // Claim failure is non-fatal — user can retry.
    } finally {
      setClaiming(false);
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (!season) {
    return (
      <div style={{ padding: '0 16px', marginBottom: 6 }}>
        <div className="chunk" style={{ padding: '10px 14px', position: 'relative', opacity: 0.45 }}>
          <span className="bolt" style={{ top: 6, left: 6 } as CSSProperties}/>
          <span className="bolt" style={{ top: 6, right: 6 } as CSSProperties}/>
          <div style={{
            fontFamily: 'var(--f-display)', fontSize: 9.5, letterSpacing: '0.18em',
            color: 'var(--tx-faint)',
          }}>{t('season.loading')}</div>
        </div>
      </div>
    );
  }

  // ── Derived values ────────────────────────────────────────────────────────
  const lvl = season.level;
  const claimable = season.claimableLevels;
  const hasClaim = claimable.length > 0;

  // Next milestone: the first milestone whose level > current level.
  const nextMilestone = season.milestones.find((m) => m.level > lvl);
  // XP within the current level bracket.
  const prevMilestoneXp = lvl === 0
    ? 0
    : (season.milestones.find((m) => m.level === lvl)?.xpRequired ?? 0);
  const nextMilestoneXp = nextMilestone?.xpRequired ?? prevMilestoneXp;
  const xpInBracket = season.xp - prevMilestoneXp;
  const bracketSize = nextMilestoneXp - prevMilestoneXp;
  // Fraction of fill within current bracket (0.0–1.0), max level = full bar.
  const pct = nextMilestone
    ? Math.min(100, bracketSize > 0 ? (xpInBracket / bracketSize) * 100 : 0)
    : 100;

  // Display list: up to 4 milestones evenly spaced along the bar.
  const displayMilestones = season.milestones.slice(0, 4);

  return (
    <div style={{ padding: '0 16px', marginBottom: 6 }}>
      <div className="chunk" style={{ padding: '10px 14px', position: 'relative' }}>
        <span className="bolt" style={{ top: 6, left: 6 } as CSSProperties}/>
        <span className="bolt" style={{ top: 6, right: 6 } as CSSProperties}/>

        {/* ── Header row ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontFamily: 'var(--f-display)', fontSize: 9.5, letterSpacing: '0.18em',
              color: 'var(--gold-hi)', textShadow: '0 0 6px var(--gold-shadow)',
            }}>{t('season.header')}</span>
            <span style={{ fontFamily: 'var(--f-display)', fontSize: 12, color: '#fff', letterSpacing: '0.04em' }}>
              {t('season.name')}
            </span>
            <span style={{
              fontFamily: 'var(--f-display)', fontSize: 8.5, letterSpacing: '0.14em',
              color: 'var(--cyan)', padding: '1.5px 5px',
              border: '0.5px solid rgba(61,213,255,0.5)', borderRadius: 3,
            }}>
              {t('season.level').replace('{n}', String(lvl))}
            </span>
          </div>
          {nextMilestone ? (
            <span style={{ fontFamily: 'var(--f-num)', fontSize: 10, color: 'var(--tx-faint)', letterSpacing: '0.08em' }}>
              {t('season.xpToNext')
                .replace('{n}', String(nextMilestone.level))
                .replace('{xp}', String(Math.max(0, nextMilestoneXp - season.xp)))}
            </span>
          ) : (
            <span style={{ fontFamily: 'var(--f-num)', fontSize: 10, color: 'var(--gold-hi)', letterSpacing: '0.08em' }}>
              {t('season.maxLevel')}
            </span>
          )}
        </div>

        {/* ── Progress track ── */}
        <div style={{ position: 'relative', height: 22 }}>
          {/* Track background */}
          <div style={{
            position: 'absolute', top: 9, left: 12, right: 12, height: 4, borderRadius: 2,
            background: 'rgba(0,0,0,0.5)',
            boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
          }}/>
          {/* Fill */}
          <div style={{
            position: 'absolute', top: 9, left: 12, height: 4, borderRadius: 2,
            width: `calc((100% - 24px) * ${pct / 100})`,
            background: 'linear-gradient(90deg, var(--gold-deep), var(--gold-hi))',
            boxShadow: '0 0 8px var(--gold-shadow)',
            transition: 'width 0.4s ease',
          }}/>
          {/* Milestone dots */}
          {displayMilestones.map((m, i) => {
            const atFrac = m.level / MAX_LEVEL;
            const isClaimed = season.claimedLevels.includes(m.level);
            const isClaimable = claimable.includes(m.level);
            const isNext = m.level === nextMilestone?.level;
            const col = MILESTONE_COLS[i % MILESTONE_COLS.length];
            const icon = MILESTONE_ICONS[i % MILESTONE_ICONS.length];
            const size = isNext ? 22 : 18;
            return (
              <div key={m.level} style={{
                position: 'absolute', top: 0,
                left: `calc(12px + (100% - 24px) * ${atFrac})`,
                transform: 'translateX(-50%)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              }}>
                <div style={{
                  width: size, height: size,
                  borderRadius: '50%',
                  background: isClaimed
                    ? `radial-gradient(circle at 30% 25%, ${col}cc, color-mix(in srgb, ${col} 40%, #000))`
                    : isClaimable
                      ? `radial-gradient(circle at 30% 25%, ${col}88, #14213b 70%, #050a14)`
                      : 'radial-gradient(circle at 30% 25%, #4a5670, #14213b 70%, #050a14)',
                  boxShadow: isNext
                    ? `0 0 12px ${col}aa, inset 0 1px 0 rgba(255,255,255,0.45), 0 0 0 2px ${col}`
                    : isClaimed
                      ? `inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -2px 3px rgba(0,0,0,0.5), 0 0 0 1.5px ${col}88`
                      : isClaimable
                        ? `0 0 8px ${col}66, 0 0 0 1.5px ${col}`
                        : 'inset 0 1px 0 rgba(255,255,255,0.1), 0 0 0 1px rgba(255,255,255,0.08)',
                  display: 'grid', placeItems: 'center',
                  fontFamily: 'var(--f-display)', fontSize: 9,
                  color: isClaimed ? '#0a0f1c' : isClaimable ? col : 'var(--tx-faint)',
                }}>
                  {isClaimed ? '✓' : icon}
                </div>
                <span style={{
                  fontFamily: 'var(--f-num)', fontSize: 7.5,
                  color: isNext || isClaimable ? col : 'var(--tx-faint)',
                }}>L{m.level}</span>
              </div>
            );
          })}
        </div>

        {/* ── CLAIM button — only rendered when there are claimable levels ── */}
        {hasClaim && (
          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              className="cta gold"
              onClick={handleClaim}
              disabled={claiming}
              style={{
                width: '100%',
                fontSize: 11,
                padding: '8px 12px',
                opacity: claiming ? 0.6 : 1,
                cursor: claiming ? 'not-allowed' : 'pointer',
              }}
            >
              {claiming ? '…' : `${t('season.claim')} L${claimable[0]}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DailyStrip ───────────────────────────────────────────────────────────────
// Quick-affordance strip: energy, daily deal, missions, VIP.
// TODO: wire to real daily-rewards + energy system when built.

function DailyStrip() {
  // Slots are placeholders until real systems land: energy/deal/missions/VIP.
  // Rendered as a non-interactive skeleton with a "СКОРО" tag so users don't
  // mistake them for live cells (opus-review C2 fix).
  const cells = [
    { icon: '⚡', label: 'ЭНЕРГИЯ', col: '#3dd5ff' },
    { icon: '⏳', label: 'ДЕАЛ',    col: '#ff9a3c' },
    { icon: '◎', label: 'МИССИИ',  col: '#4ad06b' },
    { icon: '◆', label: 'VIP',     col: '#f5c542' },
  ] as const;

  return (
    <div style={{ padding: '0 16px', marginTop: 6, marginBottom: 4 }}>
      <div className="chunk" style={{
        padding: 8,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 0,
        position: 'relative',
        opacity: 0.55,
        filter: 'grayscale(0.4)',
      }}>
        {cells.map((c, i) => (
          <div key={i} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '4px 4px',
            borderRight: i < 3 ? '1px solid rgba(255,255,255,0.06)' : 'none',
          }}>
            <span style={{
              fontFamily: 'var(--f-display)', fontSize: 13, color: c.col,
              textShadow: `0 0 8px ${c.col}33`,
              lineHeight: 1, marginBottom: 4,
            }}>{c.icon}</span>
            <span style={{
              fontFamily: 'var(--f-display)', fontSize: 11, color: 'var(--tx-faint)',
              lineHeight: 1, letterSpacing: '0.04em',
            }}>—</span>
            <span style={{
              fontFamily: 'var(--f-display)', fontSize: 7.5, letterSpacing: '0.16em',
              color: 'var(--tx-faint)', marginTop: 3,
            }}>{c.label}</span>
          </div>
        ))}
        <span style={{
          position: 'absolute', top: -8, right: 10,
          fontFamily: 'var(--f-display)', fontSize: 9, letterSpacing: '0.18em',
          padding: '3px 8px', borderRadius: 5,
          background: 'rgba(245,197,66,0.16)',
          color: 'var(--gold-hi)',
          boxShadow: 'inset 0 0 0 1px rgba(245,197,66,0.45)',
        }}>СКОРО</span>
      </div>
    </div>
  );
}

// ─── pluralizeDays ───────────────────────────────────────────────────────────
// RU pluralization: 1 день / 2 дня / 5 дней. Tournament weekend only ever
// needs n ∈ {1,2,3} but the rule is the standard last-digit table.

function daysKey(n: number): TKey {
  if (n === 1) return 'tournament.daysOne';
  if (n === 2) return 'tournament.daysTwo';
  return 'tournament.daysFull';
}

// ─── HomeSection ─────────────────────────────────────────────────────────────
// Full MainMenu composition: CatHero + StatTiles + SeasonStrip + DailyStrip +
// Launch CTA + Recent Contracts strip.
// Data wired to: useRunStore, useCatStore, useRunHistorySnapshot.

export function HomeSection() {
  const startRun = useRunStore((s) => s.startRun);
  const setReactorMode = useRunStore((s) => s.setReactorMode);
  const dismissReactorPrompt = useRunStore((s) => s.dismissReactorPrompt);
  const reactorPromptDismissed = useRunStore((s) => s.reactorPromptDismissed);
  const active = useCatStore((s) => s.active);
  const { stats, history } = useRunHistorySnapshot();
  const { t } = useT();

  // Phase 2i — daily challenge card. `useChallenge()` subscribes to the
  // plain-module challengeStore; renders null pre-hydrate, the card otherwise.
  const challenge = useChallenge();
  // Phase 2k — Tournament Weekend. When isActive, the regular daily challenge
  // card is replaced by the tournament card (sticky modifier already overrides
  // the daily one server-side, so the card just re-skins the affordance).
  const tournament = useTournament();
  const tournamentActive = !!(tournament && tournament.isActive);
  // Engineer ownership source-of-truth = useCatStore.active.engineer slot.
  // Mirrors how CatSelector / catStore distinguishes unlocked vs locked classes.
  const playerHasEngineer = active.engineer != null;

  // Achievements live in a non-Zustand store (module-level cache). useSyncExternalStore
  // pulls a fresh snapshot when the cache updates so the prompt appears as soon as
  // the player crosses fullClearCount === 5 on the server side.
  const achievements = useSyncExternalStore(subscribeProgress, getAchievements, getAchievements);

  const showReactorPrompt =
    achievements.fullClearCount >= REACTOR_UNLOCK_THRESHOLD &&
    !achievements.reactorEntered &&
    !reactorPromptDismissed;

  // Cats alive = count of non-null slots
  const catsAlive = Object.values(active).filter(Boolean).length;
  const totalSlots = Object.keys(active).length; // always 4

  // Win rate
  const winRatePct = stats.totalRuns > 0
    ? Math.round((stats.wins / Math.max(1, stats.wins + stats.losses)) * 100)
    : 0;

  const handleLaunch = () => {
    unlockAudio();
    haptic.impact('medium');
    startRun();
  };

  // Reactor entry mirrors the regular ▶ Play handler (unlockAudio + haptic +
  // startRun) but flips reactorMode FIRST so startRun's preserve-reactorMode
  // branch picks it up. RunScene.spawnWave then dispatches to REACTOR_FLOOR_WAVES.
  const handleEnterReactor = () => {
    unlockAudio();
    haptic.impact('heavy');
    setReactorMode(true);
    startRun();
  };

  const handleDismissReactor = () => {
    haptic.impact('light');
    dismissReactorPrompt();
  };

  // Phase 2i — start a daily-challenge run. Mirrors handleLaunch's
  // (unlockAudio + haptic + startRun) pattern but routes through
  // useRunStore.startChallengeRun so reactorMode is force-cleared and
  // forced-class (class_lock_engineer → engineer) gets applied.
  //
  // Phase 2k Fix HIGH 2 — during a tournament weekend the *tournament's*
  // sticky modifier is authoritative; the daily challenge cache may briefly
  // diverge (stale daily modifier from before lazy/cron sync, or simply not
  // yet hydrated). Using `effectiveModifierId` across display, lock check,
  // and launch payload ensures UI and submit always agree — eliminating the
  // class of silent-drop bugs where the UI showed one modifier but launched
  // with another that the server's `matches` gate then rejected.
  const effectiveModifierId =
    tournamentActive && tournament?.modifierId
      ? tournament.modifierId
      : challenge?.modifierId ?? null;
  const isChallengeClassLocked =
    effectiveModifierId === 'class_lock_engineer';
  const canPlayChallenge = !isChallengeClassLocked || playerHasEngineer;

  const handlePlayChallenge = () => {
    if (!challenge || !effectiveModifierId || !canPlayChallenge) return;
    unlockAudio();
    sfxChallengeStart();
    haptic.impact('medium');
    useRunStore.getState().startChallengeRun({
      date: challenge.date,
      modifierId: effectiveModifierId,
      seed: challenge.seed,
    });
  };

  // Most recent 5 runs
  const recentRuns = history.slice(0, 5);

  return (
    <div className="cradle-app">
      <NewsTicker />
      <GlyphDefs />
      <div className="cradle-scroll">

        {/* ── Daily quick-affordance strip ────────────────────────── */}
        <DailyStrip />

        {/* ── Season / Battle Pass strip ───────────────────────────── */}
        <SeasonStrip />

        {/* ── Phase 2k: Tournament Weekend card (preempts Daily Challenge) ──
            Fix MEDIUM 3 — рендерится даже когда challenge=null (медленная
            гидратация challengeStore). Кнопка ▶ ИГРАТЬ при этом disabled
            с фолбэк-лейблом «Загрузка…», так как handlePlayChallenge
            требует challenge.date/seed для startChallengeRun. */}
        {tournamentActive && tournament && (
          <div style={{ padding: '0 16px', marginBottom: 10 }}>
            <div
              className="chunk"
              style={{
                padding: '14px 16px',
                border: '1px solid rgba(245,197,66,0.55)',
                boxShadow:
                  '0 0 28px rgba(245,197,66,0.18) inset, 0 8px 16px rgba(0,0,0,0.5)',
              }}
            >
              <div
                style={{
                  fontFamily: '"Russo One", sans-serif',
                  fontSize: 10,
                  letterSpacing: '0.18em',
                  color: '#ffd866',
                  marginBottom: 6,
                }}
              >
                {t('tournament.header')}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontFamily: '"Russo One", sans-serif',
                    letterSpacing: '0.04em',
                  }}
                >
                  {effectiveModifierId
                    ? t(`challenge.modifier.${effectiveModifierId}.name` as TKey)
                    : ''}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                  {effectiveModifierId
                    ? t('challenge.hud.multiplier').replace(
                        '{mult}',
                        MODIFIER_DEFS[effectiveModifierId].scoreMultiplier.toFixed(2),
                      )
                    : ''}
                </div>
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  color: '#ffd866',
                  marginBottom: 8,
                  letterSpacing: '0.05em',
                }}
              >
                {t('tournament.daysRemaining').replace('{n}', t(daysKey(tournament.daysRemaining)))}
              </div>
              {tournament.yourCumulativeScore !== null && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.6)',
                    marginBottom: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    {t('tournament.cumulative')}:{' '}
                    <b style={{ color: '#fff' }}>{tournament.yourCumulativeScore}</b>
                  </span>
                  <span>
                    {t('tournament.rank')}:{' '}
                    <b style={{ color: '#fff' }}>{tournament.yourCumulativeRank ?? '—'}</b>
                  </span>
                </div>
              )}
              <div
                style={{
                  fontSize: 10,
                  color: 'rgba(245,197,66,0.7)',
                  marginBottom: 10,
                  fontStyle: 'italic',
                }}
              >
                {t('tournament.prizeHint')}
              </div>
              {isChallengeClassLocked && !playerHasEngineer && (
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(245,197,66,0.7)',
                    marginBottom: 10,
                    fontStyle: 'italic',
                  }}
                >
                  {t('challenge.card.classLockedHint')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="cta gold"
                  onClick={handlePlayChallenge}
                  disabled={!challenge || !canPlayChallenge}
                  style={{
                    flex: 1,
                    opacity: challenge && canPlayChallenge ? 1 : 0.5,
                    cursor: challenge && canPlayChallenge ? 'pointer' : 'not-allowed',
                  }}
                >
                  {challenge ? t('challenge.card.play') : t('tournament.loading')}
                </button>
                <button
                  type="button"
                  className="cta dark"
                  disabled
                  style={{ flex: 1, opacity: 0.5 }}
                >
                  {t('challenge.card.leaderboard')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Phase 2i: Daily Challenge card (hidden during tournament) ─── */}
        {!tournamentActive && challenge && (
          <div style={{ padding: '0 16px', marginBottom: 10 }}>
            <div
              className="chunk"
              style={{
                padding: '14px 16px',
                border: '1px solid rgba(245,197,66,0.35)',
                boxShadow:
                  '0 0 22px rgba(245,197,66,0.12) inset, 0 8px 16px rgba(0,0,0,0.5)',
              }}
            >
              <div
                style={{
                  fontFamily: '"Russo One", sans-serif',
                  fontSize: 10,
                  letterSpacing: '0.18em',
                  color: '#ffd866',
                  marginBottom: 6,
                }}
              >
                {t('challenge.card.header')}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontFamily: '"Russo One", sans-serif',
                    letterSpacing: '0.04em',
                  }}
                >
                  {t(`challenge.modifier.${challenge.modifierId}.name` as TKey)}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
                  {t('challenge.hud.multiplier').replace(
                    '{mult}',
                    MODIFIER_DEFS[challenge.modifierId].scoreMultiplier.toFixed(2),
                  )}
                </div>
              </div>
              {challenge.yourEntry && (
                <div
                  style={{
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.6)',
                    marginBottom: 10,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    {t('challenge.card.yourRecord')}:{' '}
                    <b style={{ color: '#fff' }}>{challenge.yourEntry.score}</b>
                  </span>
                  <span>
                    {t('challenge.card.rank')}:{' '}
                    <b style={{ color: '#fff' }}>{challenge.yourRank ?? '—'}</b>
                  </span>
                </div>
              )}
              {isChallengeClassLocked && !playerHasEngineer && (
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(245,197,66,0.7)',
                    marginBottom: 10,
                    fontStyle: 'italic',
                  }}
                >
                  {t('challenge.card.classLockedHint')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="cta gold"
                  onClick={handlePlayChallenge}
                  disabled={!canPlayChallenge}
                  style={{
                    flex: 1,
                    opacity: canPlayChallenge ? 1 : 0.5,
                    cursor: canPlayChallenge ? 'pointer' : 'not-allowed',
                  }}
                >
                  {t('challenge.card.play')}
                </button>
                <button
                  type="button"
                  className="cta dark"
                  disabled
                  style={{ flex: 1, opacity: 0.5 }}
                >
                  {t('challenge.card.leaderboard')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Phase 2h: Bastet reactor unlock prompt ───────────────── */}
        {showReactorPrompt && (
          <div style={{ padding: '0 16px', marginBottom: 10 }}>
            <div
              className="chunk"
              style={{
                padding: '16px 18px',
                border: '1px solid rgba(245,197,66,0.4)',
                boxShadow:
                  '0 0 28px rgba(245,197,66,0.16) inset, 0 10px 18px rgba(0,0,0,0.5)',
              }}
            >
              <div
                style={{
                  fontFamily: '"Russo One", sans-serif',
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  color: '#ffd866',
                  marginBottom: 6,
                }}
              >
                {t('home.reactor.label')}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.78)',
                  lineHeight: 1.45,
                  marginBottom: 12,
                }}
              >
                {t('home.reactor.body')}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="cta gold"
                  onClick={handleEnterReactor}
                  style={{ flex: 1 }}
                >
                  {t('home.reactor.enter')}
                </button>
                <button
                  type="button"
                  className="cta dark"
                  onClick={handleDismissReactor}
                  style={{ flex: 1 }}
                >
                  {t('home.reactor.defer')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Daily: Streak + Quests ───────────────────────────────── */}
        <div style={{ padding: '0 16px', marginBottom: 6 }}>
          <SectionHead title="ЕЖЕДНЕВНОЕ" accent="#3dd5ff" />
          <div className="chunk" style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <StreakRing />
            <QuestList />
          </div>
        </div>

        {/* ── Title block ──────────────────────────────────────────── */}
        <div style={{ textAlign: 'center', padding: '4px 20px 0' }}>
          <div style={{
            fontFamily: 'var(--f-display)',
            fontSize: 10, letterSpacing: '0.4em',
            color: 'var(--cyan)', opacity: 0.7,
          }}>СТАНЦИЯ · 9 ЖИЗНЕЙ</div>
          <div style={{
            fontFamily: 'var(--f-display)',
            fontSize: 38, letterSpacing: '0.06em', marginTop: 4,
            background: 'linear-gradient(180deg, #ffe48a 0%, #f5c542 45%, #b87f1a 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 24px rgba(245,197,66,0.25)',
            filter: 'drop-shadow(0 2px 0 rgba(0,0,0,0.35))',
          }}>CAT&nbsp;VS&nbsp;AI</div>
          <div style={{
            margin: '4px auto 0', width: 130, height: 1.5,
            background: 'linear-gradient(90deg, transparent, var(--gold), transparent)',
            boxShadow: '0 0 6px var(--gold)',
          }} />
        </div>

        {/* ── CatHero portrait ─────────────────────────────────────── */}
        <div style={{ marginTop: 4 }}>
          <CatHero />
        </div>

        {/* ── Hero context strip ───────────────────────────────────── */}
        <div style={{
          marginTop: -8,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
          fontFamily: 'var(--f-display)', fontSize: 10, letterSpacing: '0.18em',
        }}>
          <span style={{ color: 'var(--cyan)' }}>
            <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: 'var(--cyan)', boxShadow: '0 0 6px var(--cyan)', marginRight: 6,
            }}/>
            НИКА-7
          </span>
          <span style={{ color: 'var(--tx-faint)' }}>·</span>
          <span style={{ color: 'var(--tx-dim)' }}>
            {catsAlive} ИЗ {totalSlots} ЖИВЫ
          </span>
          <span style={{ color: 'var(--tx-faint)' }}>·</span>
          <span style={{ color: 'var(--gold-hi)' }}>★ ЭПИЧ</span>
        </div>

        {/* ── Stat tiles row ───────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, padding: '8px 16px 0' }}>
          <StatTile
            icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                <circle cx="8" cy="8" r="5" opacity="0.3"/>
                <circle cx="8" cy="8" r="2.5"/>
              </svg>
            }
            label={t('menu.catsAlive')}
            value={`${catsAlive} / ${totalSlots}`}
            accent="#3dde6a"
          />
          <StatTile
            icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M2 12 L 6 6 L 9 9 L 14 3"/>
              </svg>
            }
            label={t('menu.totalRuns')}
            value={String(stats.totalRuns || '—')}
            accent="#3dd5ff"
          />
          <StatTile
            icon={
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
                <path d="M8 1l2 5h5l-4 3 1.5 5L8 11l-4.5 3L5 9 1 6h5z"/>
              </svg>
            }
            label={t('menu.winRate')}
            value={stats.totalRuns > 0 ? `${winRatePct}%` : '—'}
            accent="#f5c542"
          />
        </div>

        {/* ── Main CTA ─────────────────────────────────────────────── */}
        <div style={{ padding: '20px 20px 8px' }}>
          <button
            className="cta block"
            style={{ fontSize: 18, padding: '18px 22px', width: '100%' } as CSSProperties}
            onClick={handleLaunch}
          >
            {t('menu.launch')}
          </button>
        </div>

        {/* ── Recent Contracts strip ───────────────────────────────── */}
        {recentRuns.length > 0 && (
          <div style={{ padding: '8px 16px 0' }}>
            <SectionHead title={t('menu.recentContracts').replace('// ', '')} accent="#3dd5ff" />
            <div className="chunk" style={{ padding: '8px 12px', marginTop: 0 }}>
              {recentRuns.map((r, i) => (
                <div key={r.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '7px 0',
                  borderTop: i > 0 ? '1px solid var(--line)' : 'none',
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                    background: r.outcome === 'won' ? 'var(--cta-green)' : 'var(--danger)',
                    boxShadow: `0 0 6px ${r.outcome === 'won' ? 'var(--cta-green)' : 'var(--danger)'}`,
                  }} />
                  <span style={{
                    fontFamily: 'var(--f-display)', fontSize: 11, letterSpacing: '0.1em',
                    color: r.outcome === 'won' ? 'var(--cta-green-hi)' : 'var(--danger)',
                  }}>
                    {r.outcome === 'won' ? t('menu.outcome.won') : t('menu.outcome.lost')}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--tx-dim)' }}>
                    {r.kills}К · {Math.floor(r.durationMs / 1000)}с
                  </span>
                  {r.loot != null && (
                    <span style={{
                      marginLeft: 'auto',
                      fontFamily: 'var(--f-num)', fontSize: 12, color: 'var(--gold-hi)',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      ◗ {r.loot}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ height: 16 }} />
      </div>
    </div>
  );
}
